import React from 'react';
import { PEDIATRIC_AGE_MAX } from './staffNameplates.ts';
import { loadLegendOpen, saveLegendOpen } from './boardPrefs.ts';
import {
  PALETTE_GROUPS, PALETTE_LABELS, PaletteKey, BORDER_PALETTE_KEYS,
  paletteColor, setPaletteColor, resetPalette, hasPaletteOverrides, isPaletteOverridden,
} from './nameplatePalette.ts';

// Collapsible legend explaining the nameplate/brick colours used across the board and bullpen.
//
// EVERY SWATCH IS ALSO THE PICKER: it has a native <input type="color"> stretched over it at zero
// opacity -- the same pattern the Hospitals page uses for a site's board colour -- so clicking the
// square opens the browser's colour dialog. The choice is saved in localStorage on this machine and
// never sent to the backend.
//
// The categories, their order and their labels all come from nameplatePalette.ts rather than being
// written out here, so the legend and the set of editable colours cannot drift apart.

const SWATCH_PX = 14;

const Swatch: React.FC<{
  pKey: PaletteKey; label: string; kind: 'fill' | 'border'; onChange: () => void;
}> = ({ pKey, label, kind, onChange }) => {
  const color = paletteColor(pKey);
  const overridden = isPaletteOverridden(pKey);
  // A border category renders as a hollow box (mirroring the case-block borders); a fill renders solid.
  const face: React.CSSProperties = kind === 'border'
    ? { position: 'absolute', inset: 0, borderRadius: '3px', border: `2px solid ${color}` }
    : { position: 'absolute', inset: 0, borderRadius: '3px', background: color, boxShadow: '0 1px 1px rgba(0,0,0,0.2)' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
      <span style={{ position: 'relative', display: 'inline-block', width: `${SWATCH_PX}px`, height: `${SWATCH_PX}px`, flexShrink: 0 }}>
        <span style={face} />
        <input
          type="color"
          value={color}
          onChange={(e) => { if (setPaletteColor(pKey, e.target.value)) onChange(); }}
          title={`${label} — click to change this colour`}
          aria-label={`Colour for ${label}`}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', padding: 0, border: 'none', background: 'none' }}
        />
      </span>
      <span style={{ fontSize: '0.72em', color: 'var(--text-secondary)' }}>
        {label}
        {overridden && <span title="Changed from the default" style={{ color: 'var(--text-faint)' }}> •</span>}
      </span>
    </div>
  );
};

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '8px' }}>
    <div style={{ fontSize: '0.62em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: 'var(--text-faint)', marginBottom: '4px' }}>{title}</div>
    {children}
  </div>
);

const ColorLegend: React.FC = () => {
  // The palette is a module store, so an edit does not re-render React by itself. This forces the
  // legend to repaint immediately; the board listens for PALETTE_CHANGED_EVENT and repaints too.
  const [, bump] = React.useState(0);
  const onChange = () => bump(v => v + 1);
  // Remember whether the legend was left open, so a hard refresh comes back to it.
  const [open, setOpen] = React.useState<boolean>(loadLegendOpen);
  const labelFor = (k: PaletteKey) =>
    k === 'pediatricBorder' ? `Pediatric (age ≤ ${PEDIATRIC_AGE_MAX})` : (PALETTE_LABELS[k] || k);
  return (
    <details open={open} onToggle={(e) => { const v = (e.currentTarget as HTMLDetailsElement).open; setOpen(v); saveLegendOpen(v); }}
      style={{ marginBottom: '12px', border: '1px solid var(--border-soft)', borderRadius: '6px', padding: '8px 10px', background: 'var(--bg-surface-raised)' }}>
      <summary style={{ cursor: 'pointer', fontSize: '0.8em', fontWeight: 600, color: 'var(--text-secondary)' }}>Color legend</summary>
      <div style={{ marginTop: '6px', marginBottom: '6px', fontSize: '0.68em', color: 'var(--text-faint)' }}>
        Click any colour to change it. Saved on this computer only.
      </div>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
        {PALETTE_GROUPS.map(g => (
          <Group key={g.title} title={g.title}>
            {/* kind is resolved PER KEY, not per group: `request` is a border category while its
                group mate `multiStart` stays a solid fill. */}
            {g.keys.map(k => (
              <Swatch key={k} pKey={k} label={labelFor(k)} kind={BORDER_PALETTE_KEYS.has(k) ? 'border' : g.kind} onChange={onChange} />
            ))}
          </Group>
        ))}
      </div>
      {hasPaletteOverrides() && (
        <button
          type="button"
          onClick={() => { resetPalette(); onChange(); }}
          style={{ marginTop: '4px', fontSize: '0.7em', padding: '3px 10px', borderRadius: '5px', cursor: 'pointer', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
        >
          Reset colors to default
        </button>
      )}
    </details>
  );
};

export default ColorLegend;
